import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationPanel = ({ notifications, onDismiss, onViewAll }) => {
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'expired': return 'AlertTriangle';
      case 'missing': return 'FileX';
      case 'approval': return 'Clock';
      case 'update': return 'Info';
      case 'success': return 'CheckCircle';
      default: return 'Bell';
    }
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high') return 'text-error';
    switch (type) {
      case 'expired': return 'text-error';
      case 'missing': return 'text-warning';
      case 'approval': return 'text-accent';
      case 'update': return 'text-primary';
      case 'success': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getNotificationBg = (type, priority) => {
    if (priority === 'high') return 'bg-error/10 border-error/20';
    switch (type) {
      case 'expired': return 'bg-error/10 border-error/20';
      case 'missing': return 'bg-warning/10 border-warning/20';
      case 'approval': return 'bg-accent/10 border-accent/20';
      case 'update': return 'bg-primary/10 border-primary/20';
      case 'success': return 'bg-success/10 border-success/20';
      default: return 'bg-muted/10 border-border';
    }
  };

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedNotifications);
    if (newExpanded?.has(id)) {
      newExpanded?.delete(id);
    } else {
      newExpanded?.add(id);
    }
    setExpandedNotifications(newExpanded);
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
          {notifications?.length > 0 && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {notifications?.length}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onViewAll}>
          <Icon name="ExternalLink" size={16} />
          View All
        </Button>
      </div>
      <div className="space-y-3">
        {notifications?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No new notifications</p>
          </div>
        ) : (
          notifications?.map((notification) => {
            const isExpanded = expandedNotifications?.has(notification?.id);
            return (
              <div
                key={notification?.id}
                className={`border rounded-lg p-4 transition-all duration-200 ${getNotificationBg(notification?.type, notification?.priority)}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full bg-background flex items-center justify-center flex-shrink-0 ${getNotificationColor(notification?.type, notification?.priority)}`}>
                    <Icon name={getNotificationIcon(notification?.type)} size={16} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-sm font-medium text-foreground">{notification?.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {isExpanded ? notification?.fullDescription : notification?.description}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {formatTime(notification?.timestamp)}
                        </span>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => onDismiss(notification?.id)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Icon name="X" size={14} />
                        </Button>
                      </div>
                    </div>

                    {notification?.fullDescription && notification?.fullDescription !== notification?.description && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => toggleExpanded(notification?.id)}
                        className="mt-2 text-xs text-primary hover:text-primary/80"
                      >
                        {isExpanded ? 'Show less' : 'Show more'}
                      </Button>
                    )}

                    {notification?.actions && notification?.actions?.length > 0 && (
                      <div className="flex items-center gap-2 mt-3">
                        {notification?.actions?.map((action, index) => (
                          <Button
                            key={index}
                            variant={action?.variant || 'outline'}
                            size="xs"
                            onClick={action?.onClick}
                          >
                            {action?.icon && <Icon name={action?.icon} size={14} />}
                            {action?.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;